/**
 * Mission table (RB §5.2.2, §5.4.2, §5.5.2, §5.8.2, §4.4.1, §4.6).
 *
 * Diff this file against the rulebook row by row — that is what it is for. Every
 * entry cites the section it came from, and the M1 test suite has one test per
 * row per outcome tier.
 *
 * As of M3 every mission in `MISSION_IDS` has a row. The mechanism that made that
 * safe while they did not is still here and still load-bearing: a mission id with
 * no entry is rejected at validation with `MISSION_NOT_IMPLEMENTED` rather than
 * silently doing nothing (§7.3).
 *
 * Note that `tn: null` means two different things and the distinction matters.
 * For `PHASE_SHIFT` it means *deterministic, no roll*. For the cyber actions and
 * `LAUNCH` it means *the TN is not a constant* — cyber rolls against the target's
 * hardening (`data/cyber.ts`) and Joint Fires against PNT constellation health
 * (`data/weapons.ts`). The resolver knows which; the `null` only says "not from
 * this column".
 *
 * `listed.exceptional` / `listed.complication` drive RB §1.4's two fallbacks —
 * an action with no listed Exceptional resolves one as a Success, and one with no
 * listed Complication resolves it as a Failure. They are applied once, in
 * `engine/roll.ts`, never per call site (§7.5).
 */
function def(m) {
    return Object.freeze(m);
}
const MISSION_LIST = Object.freeze([
    // -------------------------------------------------------------------------
    // Space Domain Awareness (RB §5.2.2)
    // -------------------------------------------------------------------------
    def({
        id: 'WIDE_SEARCH',
        cell: 'SDA',
        tn: 11,
        targetKinds: ['BAND'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'Targets a band. One HIDDEN contact there to FOUND. Exceptional: two contacts, or one straight to FIXED. Complication: a false contact enters the picture.',
    }),
    def({
        id: 'SENSOR_TASK',
        cell: 'SDA',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: 'FOUND',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: false },
        summary: 'FOUND → FIXED; against FIXED or TRACKED it refreshes the fix and resets validity. Exceptional: also CHARACTERIZED.',
    }),
    def({
        id: 'CUSTODY_WATCH',
        cell: 'SDA',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: true,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'FIXED → TRACKED and maintains it. Must be re-tasked each turn. Exceptional: holds two turns. Complication: custody lost, contact drops to FOUND.',
    }),
    def({
        id: 'CHARACTERIZE',
        cell: 'SDA',
        tn: 14,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: true,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: false },
        summary: 'Sets CHARACTERIZED. Exceptional: also reveals delta-V remaining and current mission.',
    }),
    def({
        id: 'DEBRIS_SURVEY',
        cell: 'SDA',
        tn: 11,
        targetKinds: ['BAND'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'Grants friendly assets in the band [+2] on conjunction avoidance, and returns band-wide BDA.',
    }),
    // -------------------------------------------------------------------------
    // Orbital Warfare (RB §5.4.2) and orbital mechanics (RB §3.2, §3.3, §3.5)
    // -------------------------------------------------------------------------
    def({
        id: 'PHASE_SHIFT',
        cell: 'OW',
        tn: null,
        targetKinds: ['SLOT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: { kind: 'PER_SLOT', amount: 20, maxSlots: 3 },
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'Move up to 3 slots within the band, 20 m/s per slot (RB §3.2).',
    }),
    def({
        id: 'TRANSFER',
        cell: 'OW',
        tn: null,
        targetKinds: ['SLOT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: { kind: 'TRANSFER_TABLE' },
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'Multi-turn band change. IN TRANSIT: no missions, treated as being in the destination band, Wide Search [+4] (RB §3.3).',
    }),
    def({
        id: 'SHADOW',
        cell: 'OW',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: { kind: 'FIXED', amount: 10 },
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'Move within one slot of a FIXED target and match its motion. Exceptional: matching burns are free this turn. Complication: overshoot — target drops to FOUND and the shadow is revealed at FIXED.',
    }),
    def({
        id: 'RPO',
        cell: 'OW',
        tn: 14,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: true,
        deltaV: { kind: 'FIXED', amount: 30 },
        emitsWhilePerforming: true, // RB §1.5 lists RPO as an emitting activity
        listed: { exceptional: true, complication: false },
        summary: "Close to the target's slot. Target becomes TRACKED and CHARACTERIZED. Exceptional: also reveals its queued mission.",
    }),
    def({
        id: 'BREAK_CUSTODY',
        cell: 'OW',
        tn: 14,
        targetKinds: ['SELF'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: { kind: 'FIXED', amount: 20 },
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'Exceptional: enemy knowledge drops to HIDDEN. Success: to FOUND. Complication: no change and twice the delta-V (RB §5.4.5).',
    }),
    def({
        id: 'DEPLOY',
        cell: 'OW',
        tn: null,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: true,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'Release a sub-satellite. Free, no effect on the parent, once per sub-satellite, and only from a type with deploy capacity (errata #13).',
    }),
    def({
        id: 'EMISSION_CONTROL',
        cell: 'OW',
        tn: null,
        targetKinds: ['SELF'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: '[-2] to enemy sensing, undetectable by Electronic Support, station-keeping still expended (RB §3.5).',
    }),
    def({
        id: 'EVADE',
        cell: 'OW',
        tn: null,
        targetKinds: ['SELF'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        // Free to set. It costs delta-V only if it *fires* in phase 7, which is what
        // "does not block missions" (RB §5.4.4) has to mean for a standing order.
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'Standing order. Triggers only with warning: a FIXED-or-better attacking KKV, or an MW DA-ASAT release this turn (RB §5.4.4).',
    }),
    def({
        id: 'ENGAGE',
        cell: 'OW',
        tn: 14,
        targetKinds: ['CONTACT'],
        // RB §5.4.3: "The target must be FIXED."
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'KKV only, same band and within one slot. Exceptional: target DESTROYED. Success: DESTROYED and the KKV goes OFFLINE permanently. Complication: miss, KKV OFFLINE 2 turns.',
    }),
    // -------------------------------------------------------------------------
    // Electromagnetic Warfare — effects (RB §5.5.2). The two sensing missions
    // shipped in M1 (decision D2); these are M3.
    // -------------------------------------------------------------------------
    def({
        id: 'ELECTRONIC_SUPPORT',
        cell: 'EW',
        tn: 14,
        targetKinds: ['BAND', 'REGION'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'Sweeps one band or region: one emitting HIDDEN enemy asset to FOUND. Exceptional: every emitter swept, or one to FIXED. Complication: a friendly emitter is revealed to the enemy instead.',
    }),
    def({
        id: 'ELINT',
        cell: 'EW',
        tn: 14,
        targetKinds: ['CONTACT'],
        requiresRung: 'FOUND',
        requiresEmittingTarget: true,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: false },
        summary: 'One FOUND emitting contact to FIXED and CHARACTERIZED. Exceptional: also reveals its cyber network and hardening.',
    }),
    def({
        id: 'JAMMING',
        cell: 'EW',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        // RB §1.1 permits it, and errata #8 confirms a DEGRADED jammer re-rolls at
        // [-4] and its existing jam continues on success.
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: true,
        listed: { exceptional: true, complication: true },
        summary: 'One FIXED enemy asset in range becomes DEGRADED while jamming continues. Exceptional: OFFLINE for 1 turn, which outlives the jam (errata #7). Complication: automatically attributed.',
    }),
    def({
        id: 'DAZZLE',
        cell: 'EW',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'Ground dazzler against optical ISR satellites only. Target DEGRADED while dazzling continues.',
    }),
    def({
        id: 'ELECTRONIC_PROTECTION',
        cell: 'EW',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: false },
        summary: 'Protects one friendly asset: enemy jamming rolls against it take [-4]. Exceptional: [-8], and the jammer’s band is revealed.',
    }),
    def({
        id: 'SPOOF',
        cell: 'EW',
        tn: 14,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'One FIXED and CHARACTERIZED enemy sensor asset: inserts a false contact into the enemy picture, behaving as RB §5.2.3.',
    }),
    // -------------------------------------------------------------------------
    // Cyber (RB §5.6.3–5.6.5). TNs are offsets on the target's hardening and live
    // in `data/cyber.ts`; `tn: null` here means "not a flat TN", not "no roll".
    // -------------------------------------------------------------------------
    def({
        id: 'RECONNOITER',
        cell: 'CW',
        tn: null,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: true },
        summary: "Reveals the target's hardening rating (base −3).",
    }),
    def({
        id: 'GAIN_ACCESS',
        cell: 'CW',
        tn: null,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'Rolled against the network router: NO ACCESS → FOOTHOLD. Exceptional: also USER on one asset. Complication: detected and attributed.',
    }),
    def({
        id: 'ESCALATE_PRIVILEGE',
        cell: 'CW',
        tn: null,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: true },
        summary: 'Climbs one rung on one asset (base +1 USER, +3 ADMIN). Complication: attributed.',
    }),
    def({
        id: 'DEPLOY_PAYLOAD',
        cell: 'CW',
        tn: null,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: true },
        summary: 'Delivers Collect, Deny, Corrupt, Hijack or Wiper (base +1). See RB §5.6.4.',
    }),
    def({
        id: 'ESTABLISH_PERSISTENCE',
        cell: 'CW',
        tn: null,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: true },
        summary: "The defender's Hunt takes [-4]; one free re-entry at FOOTHOLD if access is lost.",
    }),
    def({
        id: 'HUNT',
        cell: 'CW',
        tn: 14,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'Reveals attacker access on one friendly asset and removes one rung. Exceptional: removes all and identifies the attacking network. Complication: reports "no compromise detected" regardless.',
    }),
    def({
        id: 'HARDEN',
        cell: 'CW',
        tn: null,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'VULNERABLE → NORMAL, or NORMAL → HARDENED. Reveals the rating to the defender.',
    }),
    def({
        id: 'MONITOR',
        cell: 'CW',
        tn: null,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'HARDENED → MONITORED. Every attacker action against it is then detected.',
    }),
    def({
        id: 'ISOLATE',
        cell: 'CW',
        tn: null,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'Blocks lateral movement to and from the asset and all payloads against it. While isolated it receives no orders.',
    }),
    def({
        id: 'RESTORE',
        cell: 'CW',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'Halves the remaining recovery of one cyber-caused DEGRADED or OFFLINE effect, rounding down (decision M3-D18).',
    }),
    // -------------------------------------------------------------------------
    // Joint Fires (RB §5.7)
    // -------------------------------------------------------------------------
    def({
        id: 'LAUNCH',
        cell: 'FIRES',
        // RB §5.7.6 gates the TN on PNT constellation health: NOMINAL auto-hits,
        // DEGRADED is TN 11, OFFLINE is TN 17. Resolved from `PNT_ENGAGEMENT_TN`.
        tn: null,
        targetKinds: ['CONTACT'],
        // RB §5.7.1: a space asset must be TRACKED; a ground asset FIXED or better.
        // The stricter of the two is checked per target segment in `validate.ts`.
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'Fires one round from the launcher magazine. Arrives next turn (RB §5.3.3); terminal acquisition is re-checked on arrival (RB §5.7.2).',
    }),
    def({
        id: 'INTERCEPT',
        cell: 'FIRES',
        tn: null,
        targetKinds: ['MW_EVENT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: true,
        listed: { exceptional: false, complication: false },
        summary: 'Commits up to 4 missiles against a released inbound track. All are expended and all roll; any success intercepts (RB §5.7.5).',
    }),
    // -------------------------------------------------------------------------
    // ISR (RB §5.8.2)
    // -------------------------------------------------------------------------
    def({
        id: 'AREA_SEARCH',
        cell: 'ISR',
        tn: 14,
        targetKinds: ['REGION'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'One HIDDEN ground asset in the area to FOUND, with the search persistence bonus. Exceptional: two, or one straight to FIXED. Complication: a false ground contact enters the picture.',
    }),
    def({
        id: 'POINT_COLLECTION',
        cell: 'ISR',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: 'FOUND',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: false },
        summary: 'FOUND → FIXED. Exceptional: also CHARACTERIZED.',
    }),
    def({
        id: 'DWELL',
        cell: 'ISR',
        tn: 14,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: true,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'FIXED → TRACKED and maintains it. Must be re-tasked each turn. Exceptional: holds two turns. Complication: custody lost, target drops to FOUND.',
    }),
    def({
        id: 'TARGET_DEVELOPMENT',
        cell: 'ISR',
        tn: 14,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: false },
        summary: 'Sets CHARACTERIZED, revealing damage class. Exceptional: also reveals magazine remaining and whether the asset has fired.',
    }),
    def({
        id: 'BATTLE_DAMAGE_ASSESSMENT',
        cell: 'ISR',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: 'FOUND',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'Resolves the believed status of an engaged asset back to certain knowledge (RB §6, errata #2).',
    }),
    def({
        id: 'PATTERN_OF_LIFE',
        cell: 'ISR',
        tn: 14,
        targetKinds: ['CONTACT'],
        requiresRung: 'FIXED',
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: true, complication: true },
        summary: 'MOBILE or RELOCATABLE target at FIXED: the fix lasts 3 turns and survives one Relocate. Exceptional: also reveals the region it will relocate into. Complication: the reported location is one region off.',
    }),
    // -------------------------------------------------------------------------
    // Ground manoeuvre (RB §4.4.1) and TT&C (RB §4.6)
    // -------------------------------------------------------------------------
    def({
        id: 'RELOCATE',
        cell: 'ISR',
        tn: null,
        targetKinds: ['REGION', 'SELF'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: 'MOBILE: one turn. RELOCATABLE: two turns. On completion enemy knowledge drops to FOUND (RB §4.4.1).',
    }),
    def({
        id: 'TTC_REASSIGN',
        cell: 'CREWCOM',
        tn: 11,
        targetKinds: ['CONTACT'],
        requiresRung: null,
        requiresEmittingTarget: false,
        forbiddenWhenDegraded: false,
        deltaV: null,
        emitsWhilePerforming: false,
        listed: { exceptional: false, complication: false },
        summary: "Reassign a satellite to a new TT&C station. The *new* station rolls and the old station's state is irrelevant (errata #11).",
    }),
]);
export const MISSIONS = Object.freeze(Object.fromEntries(MISSION_LIST.map((m) => [m.id, m])));
export const IMPLEMENTED_MISSION_IDS = Object.freeze(MISSION_LIST.map((m) => m.id));
export function missionDef(id) {
    return MISSIONS[id];
}
/**
 * `RELOCATE` is owned by whichever cell the scenario assigns the asset to; the
 * `cell` field above is only the *default* used by the order builder. Cell
 * authority is enforced from `asset.assignedCell` (§3.3 invariant 2), never from
 * this table — which is why `validate.ts` never reads `MissionDef.cell`.
 */
export const MISSION_CELL_IS_ADVISORY = true;
//# sourceMappingURL=missions.js.map